import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';

/** Uma linha por lead, como vem de GET /gestor-ia/leads-site/relatorio (linhaDoRelatorio no servidor). */
export interface LeadDoRelatorio {
  id: number;
  nome: string;
  empresa: string;
  telefone: string;
  /** 'YYYY-MM-DD HH:MM': formulário ou, sem ele, colagem */
  chegouEm: string;
  /** chegouEm é a hora real do formulário (leads colados antes de 26/09/2026 só têm a hora da colagem) */
  horaDoFormulario: boolean;
  temComputador: boolean | null;
  fonte: string;
  tipo: 'Pago' | 'Orgânico' | 'Referral' | 'Manual' | string;
  campanha: string | null;
  site: string | null;
  dispositivo: string | null;
  uf: string | null;
  conversou: boolean;
  criouTeste: boolean;
  entrouNoTeste: boolean;
  etapa: string;
  motivoPerda: string | null;
}

/** Linha agregada: quantos leads e até onde chegaram no funil. */
export interface Grupo {
  rotulo: string;
  total: number;
  conversou: number;
  teste: number;
  cliente: number;
  cor?: string;
}

interface Filtro { chave: string; rotulo: string; }

type Aba = 'geral' | 'origens' | 'perfil' | 'leads';

const SEM = '—';
const TURNOS = [
  {rotulo: 'Madrugada', faixa: '00h – 05h', de: 0, ate: 5, icone: 'fa-moon', cor: '#6d3fc0'},
  {rotulo: 'Manhã', faixa: '06h – 11h', de: 6, ate: 11, icone: 'fa-coffee', cor: '#d97706'},
  {rotulo: 'Tarde', faixa: '12h – 17h', de: 12, ate: 17, icone: 'fa-sun', cor: '#2d5cb8'},
  {rotulo: 'Noite', faixa: '18h – 23h', de: 18, ate: 23, icone: 'fa-city', cor: '#b42323'},
];
const COR_DO_TIPO: Record<string, string> = {'Pago': '#d97706', 'Orgânico': '#2d5cb8', 'Referral': '#16875a', 'Manual': '#8a95a3'};
const COR_DA_FONTE: Record<string, string> = {
  'Google Ads': '#d97706', 'Google': '#f2b441', 'Instagram Ads': '#c0265a', 'Instagram': '#e5709b', 'Facebook Ads': '#1f4fd1',
  'Facebook': '#6b8cf0', 'TikTok Ads': '#1f2a37', 'TikTok': '#5f6b7a', 'ChatGPT': '#16875a', 'Direto': '#2d5cb8',
  'Botão do site': '#0f7f8c', 'Manual': '#8a95a3',
};
const PALETA = ['#6d3fc0', '#0f7f8c', '#b45309', '#4a2ba0', '#16875a', '#c0265a', '#5f6b7a'];
const ETAPAS: Record<string, string> = {
  NOVO: 'Novo', CONVERSA: 'Em conversa', TESTE: 'Teste criado', TESTE_ATIVO: 'Teste ativo', CLIENTE: 'Cliente', PERDIDO: 'Perdido',
};

/** Até onde o lead chegou: 0 lead, 1 conversou, 2 criou o teste, 3 entrou no teste, 4 cliente. Cumulativo, para o funil nunca subir. */
function alcance(l: LeadDoRelatorio): number {
  if (l.etapa === 'CLIENTE') return 4;
  if (l.entrouNoTeste) return 3;
  if (l.criouTeste) return 2;
  if (l.conversou) return 1;
  return 0;
}

function turnoDe(l: LeadDoRelatorio) {
  const hora = Number(l.chegouEm.slice(11, 13));
  return TURNOS.find(t => hora >= t.de && hora <= t.ate);
}

function dataLocal(d: Date): string {
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}

@Component({
  selector: 'app-relatorio-leads-mia-gestor',
  templateUrl: './relatorio-leads-mia-gestor.component.html',
  styleUrls: ['./relatorio-leads-mia-gestor.component.scss'],
})
export class RelatorioLeadsMiaGestorComponent implements OnInit {
  readonly periodos = [{dias: 7, rotulo: '7 dias'}, {dias: 30, rotulo: '30 dias'}, {dias: 90, rotulo: '90 dias'}, {dias: 365, rotulo: '12 meses'}];
  readonly turnos = TURNOS;
  readonly corDoTipo = COR_DO_TIPO;
  readonly filtros: Filtro[] = [
    {chave: 'fonte', rotulo: 'Fonte'}, {chave: 'tipo', rotulo: 'Pago/orgânico'}, {chave: 'campanha', rotulo: 'Campanha'},
    {chave: 'site', rotulo: 'Site'}, {chave: 'dispositivo', rotulo: 'Dispositivo'}, {chave: 'uf', rotulo: 'Estado'},
    {chave: 'computador', rotulo: 'Computador'}, {chave: 'turno', rotulo: 'Turno'}, {chave: 'etapa', rotulo: 'Etapa'},
  ];

  aba: Aba = 'geral';
  dias = 30;
  busca = '';
  filtrosAbertos = false;
  escolhidos: Record<string, string> = {};
  opcoes: Record<string, string[]> = {};

  todos: LeadDoRelatorio[] = [];
  leads: LeadDoRelatorio[] = [];
  carregando = false;
  erro = '';

  // ===== agregados do que está filtrado =====
  funil: {rotulo: string; total: number; pct: number; passagem: number | null; cor: string; forma: string}[] = [];
  ondaCaminho = '';
  ondaLinha = '';
  comComputador = 0;
  semComputador = 0;
  hoje = 0;
  porFonte: Grupo[] = [];
  porTipo: Grupo[] = [];
  porCampanha: Grupo[] = [];
  porSite: Grupo[] = [];
  porDispositivo: Grupo[] = [];
  porEstado: Grupo[] = [];
  /** os 12 estados com mais leads (o resto vira uma nota) */
  principaisEstados: Grupo[] = [];
  semCampanha = 0;
  porMotivo: Grupo[] = [];
  porComputador: Grupo[] = [];
  serie: {rotulo: string; dica: string; total: number; partes: {tipo: string; total: number}[]}[] = [];
  serieMaximo = 1;
  serieMensal = false;
  porHora: number[] = [];
  horaMaximo = 1;
  melhorHora = -1;
  comHora = 0;
  porTurno: {rotulo: string; faixa: string; icone: string; cor: string; total: number; pct: number}[] = [];

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.carregue();
  }

  carregue() {
    this.carregando = true; this.erro = '';
    this.http.get<{leads: LeadDoRelatorio[]}>(`/gestor-ia/leads-site/relatorio?dias=${this.dias}`).subscribe({
      next: r => {
        this.carregando = false;
        this.todos = r.leads || [];
        this.monteOpcoes();
        this.recalcule();
      },
      error: e => { this.carregando = false; this.erro = e?.error?.erro || 'Não foi possível carregar o relatório de leads.'; },
    });
  }

  /** Baixa o CSV das conversas do mesmo período da tela (a sessão autentica o GET). O servidor limita a 90 dias. */
  exporteConversas() {
    window.location.href = `/gestor-ia/leads-site/exportar-conversas?dias=${this.dias}`;
  }

  mudePeriodo(dias: number) {
    if (this.dias === dias || this.carregando) return;
    this.dias = dias;
    this.carregue();
  }

  // ===== filtros =====
  valorDo(l: LeadDoRelatorio, chave: string): string {
    switch (chave) {
      case 'computador': return l.temComputador === null ? 'Não informado' : l.temComputador ? 'Com computador' : 'Sem computador';
      case 'turno': return l.horaDoFormulario ? turnoDe(l)?.rotulo || SEM : 'Sem horário';
      case 'etapa': return ETAPAS[l.etapa] || l.etapa;
      case 'dispositivo': return l.dispositivo || 'Não informado';
      case 'campanha': return l.campanha || 'Sem campanha';
      case 'site': return l.site || 'Sem site (WhatsApp/manual)';
      case 'uf': return l.uf || 'DDD desconhecido';
      default: return (l as any)[chave] || SEM;
    }
  }

  escolha(chave: string, valor: string) {
    if (valor) this.escolhidos[chave] = valor; else delete this.escolhidos[chave];
    this.recalcule();
  }

  /** Clique numa linha de gráfico filtra por ela (clicar de novo tira). */
  alterne(chave: string, valor: string) {
    this.escolha(chave, this.escolhidos[chave] === valor ? '' : valor);
  }

  limpe() {
    this.escolhidos = {};
    this.busca = '';
    this.recalcule();
  }

  get ativos(): {chave: string; rotulo: string; valor: string}[] {
    return this.filtros.filter(f => this.escolhidos[f.chave]).map(f => ({...f, valor: this.escolhidos[f.chave]}));
  }

  private monteOpcoes() {
    for (const f of this.filtros) {
      const contagem: Record<string, number> = {};
      for (const l of this.todos) { const v = this.valorDo(l, f.chave); contagem[v] = (contagem[v] || 0) + 1; }
      this.opcoes[f.chave] = Object.keys(contagem).sort((a, b) => contagem[b] - contagem[a] || a.localeCompare(b));
      // filtro que ficou sem opção no período novo sai
      if (this.escolhidos[f.chave] && !contagem[this.escolhidos[f.chave]]) delete this.escolhidos[f.chave];
    }
  }

  recalcule() {
    const termo = this.busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    this.leads = this.todos.filter(l =>
      Object.keys(this.escolhidos).every(chave => this.valorDo(l, chave) === this.escolhidos[chave])
      && (!termo || `${l.nome} ${l.empresa}`.toLowerCase().includes(termo) || (digitos.length >= 3 && l.telefone.includes(digitos))));
    const leads = this.leads;
    const hoje = dataLocal(new Date());
    this.hoje = leads.filter(l => l.chegouEm.startsWith(hoje)).length;
    this.comComputador = leads.filter(l => l.temComputador === true).length;
    this.semComputador = leads.filter(l => l.temComputador === false).length;
    this.monteFunil();
    this.porFonte = this.agrupe(l => l.fonte, g => COR_DA_FONTE[g]);
    this.porTipo = this.agrupe(l => l.tipo, g => COR_DO_TIPO[g]);
    this.porCampanha = this.agrupe(l => l.campanha);
    this.semCampanha = leads.filter(l => !l.campanha).length;
    this.porSite = this.agrupe(l => this.valorDo(l, 'site'));
    this.porDispositivo = this.agrupe(l => this.valorDo(l, 'dispositivo'));
    this.porEstado = this.agrupe(l => this.valorDo(l, 'uf'));
    this.principaisEstados = this.porEstado.slice(0, 12);
    this.porComputador = this.agrupe(l => this.valorDo(l, 'computador'),
      g => ({'Com computador': '#16875a', 'Sem computador': '#b42323'})[g] || '#cdd5de');
    this.porMotivo = this.agrupe(l => l.etapa === 'PERDIDO' ? (l.motivoPerda || 'Sem motivo anotado') : null);
    this.monteSerie();
    this.monteHoras();
  }

  /** Agrupa os leads filtrados por uma chave (null = fora), com os marcos do funil e cor fixa por rótulo. */
  private agrupe(chave: (l: LeadDoRelatorio) => string | null, cor?: (rotulo: string) => string): Grupo[] {
    const grupos: Record<string, Grupo> = {};
    for (const l of this.leads) {
      const rotulo = chave(l);
      if (rotulo === null) continue;
      const nome = rotulo || SEM;
      const g = grupos[nome] || (grupos[nome] = {rotulo: nome, total: 0, conversou: 0, teste: 0, cliente: 0});
      const a = alcance(l);
      g.total++;
      if (a >= 1) g.conversou++;
      if (a >= 2) g.teste++;
      if (a >= 4) g.cliente++;
    }
    return Object.values(grupos).sort((a, b) => b.total - a.total || a.rotulo.localeCompare(b.rotulo))
      .map((g, i) => ({...g, cor: cor?.(g.rotulo) || PALETA[i % PALETA.length]}));
  }

  private monteFunil() {
    const total = this.leads.length;
    const etapas = [
      {rotulo: 'Leads', cor: '#2d5cb8'}, {rotulo: 'Conversaram', cor: '#0f7f8c'}, {rotulo: 'Criaram teste', cor: '#6d3fc0'},
      {rotulo: 'Entraram no painel', cor: '#4a2ba0'}, {rotulo: 'Clientes', cor: '#16875a'},
    ];
    const contagem = [0, 0, 0, 0, 0];
    for (const l of this.leads) { const a = alcance(l); for (let i = 0; i <= a; i++) contagem[i]++; }
    // funil clássico: cada camada é um trapézio cuja boca é a etapa e o fundo é a etapa seguinte (mínimo de 6% pra não sumir)
    const largo = (i: number) => Math.max(6, total ? (contagem[i] ?? contagem[contagem.length - 1]) * 100 / total : 0);
    const trapezio = (i: number) => {
      const topo = largo(i), fundo = largo(i + 1), t = (100 - topo) / 2, f = (100 - fundo) / 2;
      return `polygon(${t}% 0, ${100 - t}% 0, ${100 - f}% 100%, ${f}% 100%)`;
    };
    this.funil = etapas.map((e, i) => ({
      ...e, total: contagem[i], pct: total ? Math.round(contagem[i] * 100 / total) : 0,
      passagem: i === 0 ? null : contagem[i - 1] ? Math.round(contagem[i] * 100 / contagem[i - 1]) : 0,
      forma: trapezio(i),
    }));
    // onda contínua: um ponto no meio de cada etapa, altura pela fração dos leads
    const largura = 100 / etapas.length;
    const pontos = contagem.map((c, i) => ({x: largura * i + largura / 2, y: 40 - (total ? c / total : 0) * 34}));
    const todos = [{x: 0, y: pontos[0].y}, ...pontos, {x: 100, y: pontos[pontos.length - 1].y}];
    let linha = `M 0 ${todos[0].y}`;
    for (let i = 1; i < todos.length; i++) {
      const meio = (todos[i - 1].x + todos[i].x) / 2;
      linha += ` C ${meio} ${todos[i - 1].y}, ${meio} ${todos[i].y}, ${todos[i].x} ${todos[i].y}`;
    }
    this.ondaLinha = linha;
    this.ondaCaminho = `${linha} L 100 40 L 0 40 Z`;
  }

  /** Colunas empilhadas por tipo: diária até 90 dias, mensal em 12 meses. Dias sem lead aparecem vazios. */
  private monteSerie() {
    this.serieMensal = this.dias > 90;
    const baldes: string[] = [];
    const agora = new Date();
    if (this.serieMensal) {
      for (let i = 11; i >= 0; i--) baldes.push(dataLocal(new Date(agora.getFullYear(), agora.getMonth() - i, 1)).slice(0, 7));
    } else {
      for (let i = this.dias - 1; i >= 0; i--) baldes.push(dataLocal(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - i)));
    }
    const tipos = Object.keys(COR_DO_TIPO);
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    this.serie = baldes.map(b => {
      const doBalde = this.leads.filter(l => l.chegouEm.startsWith(b));
      const rotulo = this.serieMensal ? meses[Number(b.slice(5, 7)) - 1] : `${b.slice(8, 10)}/${b.slice(5, 7)}`;
      const partes = tipos.map(tipo => ({tipo, total: doBalde.filter(l => l.tipo === tipo).length})).filter(p => p.total);
      return {rotulo, total: doBalde.length, partes,
        dica: `${rotulo}: ${doBalde.length} lead(s)` + (partes.length ? ' · ' + partes.map(p => `${p.tipo} ${p.total}`).join(', ') : '')};
    });
    this.serieMaximo = Math.max(1, ...this.serie.map(s => s.total));
  }

  /** Hora do dia só com leads que trazem o horário do formulário; os antigos têm a hora da colagem. */
  private monteHoras() {
    const comHora = this.leads.filter(l => l.horaDoFormulario);
    this.comHora = comHora.length;
    this.porHora = Array.from({length: 24}, (_, h) => comHora.filter(l => Number(l.chegouEm.slice(11, 13)) === h).length);
    this.horaMaximo = Math.max(1, ...this.porHora);
    this.melhorHora = this.comHora ? this.porHora.indexOf(Math.max(...this.porHora)) : -1;
    this.porTurno = TURNOS.map(t => {
      const total = comHora.filter(l => turnoDe(l) === t).length;
      return {...t, total, pct: this.comHora ? Math.round(total * 100 / this.comHora) : 0};
    });
  }

  // ===== apresentação =====
  pct(parte: number, total: number): number {
    return total ? Math.round(parte * 100 / total) : 0;
  }

  largura(parte: number, grupos: Grupo[]): number {
    const maior = grupos.length ? grupos[0].total : 0;
    return maior ? Math.max(2, parte * 100 / maior) : 0;
  }

  rotuloEtapa(etapa: string): string {
    return ETAPAS[etapa] || etapa;
  }

  dataCurta(chegouEm: string): string {
    return `${chegouEm.slice(8, 10)}/${chegouEm.slice(5, 7)}`;
  }

  telefoneFormatado(t: string): string {
    const d = String(t || '').replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return t;
  }

  corDaFonte(fonte: string): string {
    return COR_DA_FONTE[fonte] || '#5f6b7a';
  }

  porRotulo(_: number, g: {rotulo: string}) { return g.rotulo; }
  porId(_: number, l: LeadDoRelatorio) { return l.id; }
}
